'use client'

import * as React from 'react'
import { ModalBackTitle } from 'app/components/ModalBackTitle'
import { Bars2Icon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { ViewColumnsIcon } from '@heroicons/react/24/outline'
import { Column } from '@tanstack/react-table'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ChangeEvent, useState } from 'react'
import { DndProvider, useDragLayer } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from 'app/components/ui/dialog'
import { Button } from 'app/components/ui/button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { Checkbox } from 'app/components/ui/checkbox'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { Field, FieldLabel } from 'app/components/ui/field'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { cn } from 'app/lib/utils'
import DraggableColumn from 'app/components/table/DraggableColumn'

export type TableColumnSelectorComponentProps<T> = {
  hideTrigger?: boolean
  /** Draws Back in the title, to the dialog that opened the editor. */
  backTo?: { to: string; onBack: () => void }
  openDialog: boolean
  setOpenDialog: (open: boolean) => void
  searchValue: string
  openGroups: number[]
  filterGroups: Record<string, Column<T, unknown>[]>
  newColumnVisibility: { [k: string]: boolean } | undefined
  selectedColumns: Column<T, unknown>[]
  handleReorderColumn: (draggedColumnId: string, targetColumnId: string) => void
  onVisibilityChange: (columnId: string) => void
  handleApplyClick: () => void
  handleSearch: (event: ChangeEvent<HTMLInputElement>) => void
  handleResetToDefault: () => void
  handleClose: () => void
  setNewColumnVisibility: React.Dispatch<React.SetStateAction<{ [k: string]: boolean } | undefined>>
}

function ColumnGroup<T>({
  groupName,
  filters,
  index,
  searchValue,
  openGroups,
  newColumnVisibility,
  onVisibilityChange,
}: {
  groupName: string
  filters: Column<T, unknown>[]
  index: number
  searchValue: string
  openGroups: number[]
  newColumnVisibility: { [k: string]: boolean } | undefined
  onVisibilityChange: (columnId: string) => void
}) {
  const forceOpen = openGroups.includes(index)
  const [manualOpen, setManualOpen] = useState(index === 0)
  const isOpen = forceOpen || manualOpen

  const filteredColumns = filters.filter((col) =>
    col.columnDef.header?.toString().toLowerCase().includes(searchValue.toLowerCase()),
  )

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setManualOpen}>
      <Collapsible.Trigger className="flex w-full items-center justify-between px-4 py-3 text-strong">
        <div className="text-ui-sm">{groupName}</div>
        <ChevronDownIcon className={cn(`h-5 w-5 transition-transform`, isOpen && `rotate-180`)} />
      </Collapsible.Trigger>
      <Collapsible.Content>
        {filteredColumns.map((column) => (
          <div key={column.id} className="py-2 pl-8">
            <Field orientation="horizontal">
              <Checkbox
                id={`col-${column.id}`}
                disabled={!column.getCanHide()}
                checked={newColumnVisibility?.[column.id] ?? false}
                onCheckedChange={() => onVisibilityChange(column.id)}
              />
              <FieldLabel
                htmlFor={`col-${column.id}`}
                aria-label={column.columnDef.header?.toString()}
                className={cn(
                  'text-default font-normal! text-sm! mb-0! leading-none!',
                  column.getCanHide() ? 'cursor-pointer' : 'cursor-default',
                )}
              >
                {column.columnDef.header?.toString()}
              </FieldLabel>
            </Field>
          </div>
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

/**
 * Floating preview that follows the pointer while dragging. TouchBackend (mobile)
 * renders no native drag image, so without this the dragged row just vanishes.
 * Only rendered on mobile — desktop's HTML5Backend supplies its own ghost.
 */
function ColumnDragLayer() {
  const { isDragging, item, offset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as { column?: Column<any, unknown> } | null,
    offset: monitor.getSourceClientOffset(),
  }))

  const header = item?.column?.columnDef?.header?.toString()
  if (!isDragging || !offset || !header) return null

  return (
    <div
      className="pointer-events-none fixed left-0 top-0 z-60 translate-x-(--column-drag-x) translate-y-(--column-drag-y)"
      style={{ '--column-drag-x': `${offset.x}px`, '--column-drag-y': `${offset.y}px` } as React.CSSProperties}
    >
      <div className="flex h-9 w-65 items-center gap-2 rounded-md bg-surface-raised px-2 shadow-elevation-4 ring-1 ring-blue-300">
        <Bars2Icon className="h-5 w-5 fill-current text-default" />
        <span className="truncate text-body-sm text-default">{header}</span>
      </div>
    </div>
  )
}

export function TableColumnSelectorComponent<T>({
  hideTrigger,
  backTo,
  openDialog,
  setOpenDialog,
  searchValue,
  openGroups,
  filterGroups,
  newColumnVisibility,
  selectedColumns,
  handleReorderColumn,
  onVisibilityChange,
  handleApplyClick,
  handleSearch,
  handleResetToDefault,
  handleClose,
  setNewColumnVisibility,
}: TableColumnSelectorComponentProps<T>) {
  const isMobile = useIsMobile()
  return (
    <Dialog open={openDialog} onOpenChange={(open) => (open ? setOpenDialog(true) : handleClose())}>
      {/* Icon-only in the toolbar; the label returns below `md`, where this control
          is stacked in the "Table options" sheet and needs naming. */}
      {!hideTrigger && (
        <TooltipWrapper message="Edit columns" placement="bottom">
          <DialogTrigger asChild>
            <Button aria-label="Edit Columns Button" variant="white" size="sm" className="md:px-2">
              <ViewColumnsIcon className="h-4 w-4" />
              <div className="text-ui-xs md:hidden">Edit Columns</div>
            </Button>
          </DialogTrigger>
        </TooltipWrapper>
      )}
      <DialogContent className="max-w-180 gap-0 p-0">
        <DndProvider
          key={isMobile ? `touch` : `html5`}
          backend={isMobile ? TouchBackend : HTML5Backend}
          options={isMobile ? { enableMouseEvents: true, delayTouchStart: 150, delayMouseStart: 150 } : undefined}
        >
          {isMobile && <ColumnDragLayer />}
          <DialogHeader className="border-b border-default px-6 py-4">
            <DialogTitle>
              {backTo ? (
                <ModalBackTitle title="Edit columns" backTo={backTo.to} onBack={backTo.onBack} />
              ) : (
                `Edit columns`
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Select and reorder table columns</DialogDescription>
          </DialogHeader>
          <div className="flex max-h-column-dialog-body flex-col overflow-y-auto md:flex-row md:overflow-hidden">
            <div className="w-full md:w-1/2">
              <div className="px-4 pt-3 pb-1">
                <ControlledSearch
                  onValueChange={(val) => handleSearch({ target: { value: val } } as ChangeEvent<HTMLInputElement>)}
                  placeholder="Search"
                  className="w-full shadow-none!"
                />
              </div>
              <div className="md:max-h-column-dialog-list md:overflow-y-auto">
                {Object.entries(filterGroups).map(([groupName, filters], i) => (
                  <ColumnGroup
                    key={groupName}
                    groupName={groupName}
                    filters={filters}
                    index={i}
                    searchValue={searchValue}
                    openGroups={openGroups}
                    newColumnVisibility={newColumnVisibility}
                    onVisibilityChange={onVisibilityChange}
                  />
                ))}
              </div>
            </div>
            <div className="w-full border-t border-default bg-surface-subtle py-3 pl-3.5 pr-4 md:w-1/2 md:overflow-y-auto md:border-t-0">
              <div className="text-ui-sm text-strong">Selected columns</div>
              <div className="mt-3 flex flex-col space-y-2">
                {selectedColumns.map((col, i) => (
                  <DraggableColumn
                    key={col.id}
                    column={col}
                    index={i}
                    onDrop={handleReorderColumn}
                    onRemove={(colId) => setNewColumnVisibility((prev) => ({ ...prev, [colId]: false }))}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="border-t border-default px-6 py-3">
            <Button variant="secondary" size="md" onClick={handleResetToDefault} className="mr-auto">
              Reset to Default
            </Button>
            <Button variant="secondary" size="md" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={handleApplyClick}>
              Apply
            </Button>
          </DialogFooter>
        </DndProvider>
      </DialogContent>
    </Dialog>
  )
}
